// EDIT THIS FILE CAREFULLY!! The sideeffect of this file is huge.
import { Draft, produce } from "immer";
import React, { Dispatch, useCallback, useMemo, useReducer } from "react";
import { createContext, useContext, useContextSelector } from "use-context-selector";

type CaseReducer<S, P> = (state: Draft<S>, payload: P) => void;

type CaseReducers<S, ActionType extends string> = {
	[K in ActionType]: CaseReducer<S, any>;
};

export interface IAppAction<ActionType extends string, P = any> {
	type: ActionType;
	payload?: P;
}

type InferredActions<CR extends CaseReducers<any, any>> = {
	[K in keyof CR]: Parameters<CR[K]>[1] extends undefined | void
		? () => IAppAction<K & string, undefined>
		: (payload: Parameters<CR[K]>[1]) => IAppAction<K & string, Parameters<CR[K]>[1]>;
};

interface IAppContextType<
	S,
	ActionType extends string,
	Actions extends InferredActions<CaseReducers<S, ActionType>>,
> {
	state: S;
	dispatch: Dispatch<IAppAction<ActionType>>;
	actions: Actions;
}

export function createContextReducer<
	S,
	ActionType extends string,
	CR extends CaseReducers<S, ActionType>,
	Actions extends InferredActions<CR> = InferredActions<CR>,
>(initialState: S, caseReducers: CR) {
	const AppContext = createContext<IAppContextType<S, ActionType, Actions> | undefined>(undefined);

	const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
		const reducer = useCallback(
			(state: S, action: IAppAction<ActionType>): S => {
				return produce(state, (draft) => {
					const handler = caseReducers[action.type] as CaseReducer<S, typeof action.payload>;

					handler?.(draft, action.payload);
				});
			},
			[caseReducers]
		);

		const [state, dispatch] = useReducer(reducer, initialState);

		const generatedActions = {} as Actions;

		const actions = useMemo(() => {
			for (const type in caseReducers) {
				if (Object.hasOwn(caseReducers, type)) {
					const actionCreator = (
						payload: Parameters<CR[typeof type]>[1]
					): IAppAction<typeof type & string, Parameters<CR[typeof type]>[1]> => {
						const action: IAppAction<typeof type & string, Parameters<CR[typeof type]>[1]> = {
							type: type as typeof type & string,
							payload,
						};

						// @ts-ignore
						dispatch(action as IAppAction<ActionType, any>);

						return action;
					};

					(generatedActions as any)[type] = actionCreator;
				}
			}

			return generatedActions;
		}, [dispatch, caseReducers]);

		const value: IAppContextType<S, ActionType, Actions> = useMemo(
			() => ({
				state: state,
				dispatch,
				actions: actions,
			}),
			[state, dispatch, actions]
		);

		return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
	};

	const useContextPartial = <SelectorResult,>(
		selector: (state: S) => SelectorResult
	): { state: SelectorResult } => {
		const wrappedSelector = useCallback(
			(contextValue: IAppContextType<S, ActionType, Actions> | undefined): SelectorResult => {
				if (!contextValue) {
					throw new Error("useAppPartial must be used within AppProvider");
				}

				return selector(contextValue.state);
			},
			[selector]
		);

		const contextValueExists = useContextSelector(AppContext, (v) => !!v);

		if (!contextValueExists) {
			throw new Error("useAppPartial must be used within AppProvider");
		}

		const selectedState = useContextSelector(AppContext, wrappedSelector);

		return { state: selectedState };
	};
	const useContextActions = (): Actions => {
		const context = useContext(AppContext);

		if (!context) {
			throw new Error("useContextActions must be used within AppProvider");
		}

		return context.actions;
	};

	return { AppProvider, useContextActions, useContextPartial };
}
